import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import { acceptTeamInviteApi } from '@/api/team';
import { useTeamStore, PENDING_INVITE_KEY, type ActingAccount } from '@/store/useTeamStore';
import { useSurfaceStore } from '@/store/useSurfaceStore';
import { colors } from '@/constants/colors';

/**
 * /team/accept?token=… — the link in a Team invite email (Ray 2026-10-04).
 * Public + auth-tolerant (like /transfers/handoff): signed out → keep the
 * token and send them to sign in / sign up; the dashboard brings them back
 * here afterwards. Signed in → one button to accept.
 */
const TeamAccept: React.FC = () => {
   const [params] = useSearchParams();
   const navigate = useNavigate();
   const token = params.get('token') || '';
   const { user, isAuthenticated } = useAuthStore();
   const setActing = useTeamStore((s) => s.setActing);
   const setSurface = useSurfaceStore((s) => s.setSurface);
   const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
   const [message, setMessage] = useState('');
   const [owner, setOwner] = useState<ActingAccount | null>(null);

   // We are on the accept page now — the dashboard must not bounce back here.
   useEffect(() => {
      if (!isAuthenticated) return;
      try {
         window.localStorage.removeItem(PENDING_INVITE_KEY);
      } catch {
         /* ignore */
      }
   }, [isAuthenticated]);

   if (!token) {
      return (
         <Shell>
            <Card>
               <H>This invite link is not complete</H>
               <P>Open the link from the email again, or ask for a new invite.</P>
            </Card>
         </Shell>
      );
   }

   if (!isAuthenticated) {
      const goSignIn = () => {
         try {
            window.localStorage.setItem(PENDING_INVITE_KEY, token);
         } catch {
            /* the link still works if they open it again after signing in */
         }
         navigate('/');
      };
      return (
         <Shell>
            <Card>
               <H>You're invited to help manage vendos</H>
               <P>
                  Sign in with your own SmartFi account — or create one with the email the invite was
                  sent to. You'll come back here to accept.
               </P>
               <Btn type="button" onClick={goSignIn}>
                  Sign in or create an account
               </Btn>
            </Card>
         </Shell>
      );
   }

   const accept = async () => {
      setState('busy');
      try {
         const r = await acceptTeamInviteApi(token);
         setOwner({
            ownerId: r.workspace.ownerId,
            ownerEmail: r.workspace.ownerEmail,
            access: r.workspace.access,
            pages: r.workspace.pages ?? [],
            switches: r.workspace.switches,
         });
         setState('done');
      } catch (err: any) {
         setMessage(err?.message || 'Could not accept this invite.');
         setState('error');
      }
   };

   const openTheirs = () => {
      if (!owner) return;
      setActing(owner);
      setSurface('wifi');
      window.location.assign('/wifi');
   };

   return (
      <Shell>
         <Card>
            {state === 'done' && owner ? (
               <>
                  <H>Done — you can now help {owner.ownerEmail}</H>
                  <P>
                     Open your profile menu (your name, top right) and pick "Switch to: {owner.ownerEmail}"
                     to work on their account. Pick "Switch to: My account" to come back.
                  </P>
                  <Btn type="button" onClick={openTheirs}>
                     Open their dashboard
                  </Btn>
               </>
            ) : (
               <>
                  <H>Accept the invite?</H>
                  <P>
                     You're signed in as <b>{user?.email}</b>. You will be able to switch to the
                     account of the person who invited you and use the pages they shared. Their
                     account settings stay private.
                  </P>
                  {state === 'error' && <Err role="alert">{message}</Err>}
                  <Btn type="button" onClick={accept} disabled={state === 'busy'}>
                     {state === 'busy' ? 'Accepting…' : 'Accept invite'}
                  </Btn>
               </>
            )}
         </Card>
      </Shell>
   );
};

export default TeamAccept;

const Shell = styled.div`
   min-height: 100vh;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 16px;
`;
const Card = styled.div`
   width: 100%;
   max-width: 460px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 14px;
   padding: 24px;
`;
const H = styled.h2`
   margin: 0 0 8px;
   font-size: 20px;
`;
const P = styled.p`
   margin: 0 0 16px;
   font-size: 14px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;
const Err = styled.p`
   margin: 0 0 12px;
   font-size: 13px;
   color: #c0392b;
`;
const Btn = styled.button`
   padding: 10px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-weight: 600;
   font-size: 14px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
   }
`;
